import { Axis, axisBottom, AxisDomain, axisLeft, AxisScale } from "d3-axis";
import { ScaleLinear } from "d3-scale";
import { Selection } from "d3-selection";
import { transform } from "../../../utils/transform";
import { ChartMargin } from "../interfaces/chart-margin";
import { LineChartData } from "./line-chart";
import { LineChartSvgDataGroup } from "./line-chart-svg-data-group";

export class LineChartSvgChartArea {
    selection: Selection<SVGGElement, unknown, HTMLElement, any>;

    yAxisArea: Selection<SVGGElement, unknown, HTMLElement, any>;
    xAxisArea: Selection<SVGGElement, unknown, HTMLElement, any>;

    constructor(svg, margin) {
        this.selection = svg
            .append("g")
            .attr(
                "transform",
                "translate(" + margin.left + "," + margin.top + ")"
            );
    }

    public getXAxisArea() {
        return this.xAxisArea;
    }

    public getYAxisArea() {
        return this.yAxisArea;
    }

    public appendYAxis() {
        this.yAxisArea = this.selection
            .append("g")
            .attr("class", "uic-line-chart__y-axis");
        return this.yAxisArea;
    }

    public appendXAxis(parentHeight, margin: ChartMargin) {
        this.xAxisArea = this.selection
            .append("g")
            .attr("class", "uic-line-chart__x-axis")
            .attr(
                "transform",
                `translate(0,${parentHeight - margin.bottom - margin.top})`
            );
        return this.xAxisArea;
    }

    public appendDataGroupArea() {
        this.selection.append("g").attr("class", "uic-line-chart");
    }

    public updateChartArea(
        margin: ChartMargin,
        yAxisLabelOffset: number,
        maxYTickLabelWidth: number
    ) {
        this.selection.attr(
            "transform",
            `translate(${
                margin.left + yAxisLabelOffset + maxYTickLabelWidth
            }, ${margin.top})`
        );

        return this;
    }

    public updateYAxis(
        yScale: ScaleLinear<number, number, never>,
        yTicks: number,
        yFormatter: (y: number) => string,
        xAxisWidth: number,
        transitionDurationMs: number
    ) {
        this.selection
            .select<SVGGElement>(".uic-line-chart__y-axis")
            .transition()
            .duration(transitionDurationMs)
            .call(
                LineChartSvgChartArea.yAxis(
                    yScale,
                    yTicks,
                    yFormatter,
                    xAxisWidth
                )
            )
            .selectAll<SVGLineElement, number>("line")
            .selection()
            .classed("uic-line-chart__y-axis-grid-line", true)
            // highlight reference line for 0 if not on the x axis
            .classed(
                "uic-line-chart__y-axis-grid-line--zero",
                (i) => i === 0 && yScale.domain()[0] < 0
            );

        return this;
    }

    public updateXAxis<TScale extends AxisScale<AxisDomain>>(
        xScale: TScale,
        parentHeight: number,
        rotateXAxisLabels: boolean,
        labelRotationDegrees: number,
        additionalXTickLabelSpace: number,
        margin: ChartMargin,
        xFormatter,
        transitionDurationMs: number
    ) {
        const xAxis = this.selection.select<SVGGElement>(
            ".uic-line-chart__x-axis"
        );

        xAxis
            .transition()
            .duration(transitionDurationMs)
            .attr(
                "transform",
                `translate(0, ${
                    parentHeight -
                    margin.bottom -
                    margin.top -
                    additionalXTickLabelSpace
                })`
            )
            .call(LineChartSvgChartArea.xAxis(xScale, xFormatter));

        // x axis label direction
        const text = xAxis.selectAll("text");
        if (rotateXAxisLabels) {
            // instead of shrinking the chart we may crop the labels if they get too long, maybe use textPath with diagonal line instead as it crops the text automatically
            // see https://stackoverflow.com/questions/9241315/trimming-text-to-a-given-pixel-width-in-svg, https://bl.ocks.org/armollica/90ff1d55f7a8fdbf42500558e1c6d1f5
            text.style("text-anchor", "end")
                .attr("dx", "-.8em")
                .attr("dy", ".15em")
                .attr("transform", `rotate(-${labelRotationDegrees})`);
        } else {
            text.style("text-anchor", "inherit")
                .attr("dy", "0.71em")
                .attr("dx", null)
                .attr("transform", null);
        }

        return this;
    }

    public updateDataGroups(data: LineChartData[]) {
        const dataGroups = this.selection
            .select(".uic-line-chart")
            .selectAll(".uic-line-chart__data-group")
            .data(data)
            .join((enter) => enter.append("g"))
            .attr("class", (d) => `${d.cssClass} uic-line-chart__data-group`)
            .classed("uic-line-chart__data-group--ignore", (d) => d.ignore);

        return new LineChartSvgDataGroup(dataGroups);
    }

    public static xAxis<TScale extends AxisScale<AxisDomain>>(
        xScale: TScale,
        xFromatter: (x: string) => string
    ): Axis<AxisDomain> {
        return axisBottom(xScale)
            .tickSizeOuter(0)
            .tickFormat((val) => transform(xFromatter, val));
    }

    private static yAxis(
        yScale: ScaleLinear<number, number>,
        yTicks: number,
        yFormatter: (x: number) => string,
        width: number
    ): Axis<number | { valueOf(): number }> {
        return axisLeft(yScale)
            .ticks(yTicks)
            .tickFormat((val) => transform(yFormatter, val).toString())
            .tickSize(-width);
    }
}
